"use client";

import { useMemo } from "react";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import { hslHex } from "@/lib/music/chord-catalog";
import { ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";
import type { Tonality } from "@/lib/music/tonality";
import type { Hint, HintNote } from "@/contexts/hint-context";

/**
 * Octave anchor for the scale hints on the studio piano. The tonic is
 * placed at this octave (e.g. C4 = MIDI 60), and the rest of the scale
 * climbs from it by adding the interval in semitones, so the visible
 * strip on the keyboard always *starts* on the tonic and walks upward,
 * regardless of the key (Fa majeur → F4..E5, Sol majeur → G4..F♯5,
 * Si majeur → B4..A♯5). Without this, lighting `(tonicPc + iv) % 12`
 * inside a fixed octave would cluster the strip around C and visually
 * detach it from the actual tonic.
 */
const TONIC_OCTAVE = 4;
const TONIC_BASE_MIDI = TONIC_OCTAVE * 12 + 12; // C4 = 60

const SCALE_INTERVALS_MAJOR = [0, 2, 4, 5, 7, 9, 11] as const;
const SCALE_INTERVALS_MINOR = [0, 2, 3, 5, 7, 8, 10] as const;

/**
 * Push «&nbsp;scale&nbsp;» hints to the studio piano: the 7 scale notes
 * are tinted with a diffuse violet, the tonic gets the brighter accent.
 * No vivid chord overlay, this is the focused «&nbsp;Détail de la
 * gamme&nbsp;» view, where the chord layer would only add noise.
 *
 * Mutually exclusive with `useOverviewPianoHints` via the `enabled`
 * argument: only one harmony hint source pushes at a time, so they
 * never fight over `Piano.setHints`.
 */
export function useScalePianoHints(
  tonality: Tonality | null,
  enabled: boolean,
): void {
  const hint = useMemo<Hint | null>(() => {
    if (!enabled || !tonality) {
      return null;
    }

    const tonicPc = ROOT_PITCH_CLASS[tonality.tonic];

    if (tonicPc === undefined) {
      return null;
    }

    const intervals =
      tonality.mode === "major" ? SCALE_INTERVALS_MAJOR : SCALE_INTERVALS_MINOR;

    // Single tint for the whole scale, tonic included, no root accent.
    // Mid-saturation, mid-lightness violet : visible enough to read as
    // a halo on the keyboard without overpowering the played notes.
    const scaleHex = hslHex(260, 55, 55);
    const tonicMidi = TONIC_BASE_MIDI + tonicPc;

    const notes: HintNote[] = intervals.map((iv) => ({
      midi: tonicMidi + iv,
      color: scaleHex,
    }));

    return { source: "harmony/scale", midis: notes };
  }, [tonality, enabled]);

  useHintPublisher(hint);
}
